import React, { useCallback, useMemo } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import {
  AUDIO_ENCODING_OPTIONS,
  DEFAULT_AUDIO_TEMPLATE,
  DEFAULT_RECEIVE_AUDIO_CHANNELS,
  RECEIVE_AUDIO_CHANNELS_OPTIONS,
  type WebSocketFieldProps,
} from "./types"
import { validateAudioTemplate } from "./utils"

export const AudioFormatFields: React.FC<WebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  const audioTemplateError = useMemo(
    () => validateAudioTemplate(metadata.send_audio_template || ""),
    [metadata.send_audio_template],
  )

  const handleAudioTemplateChange = useCallback(
    (value: string) => {
      onMetadataUpdate({ send_audio_template: value })
    },
    [onMetadataUpdate],
  )

  return (
    <div className="space-y-6">
      <h4 className="text-sm font-medium text-coval-text">Audio Format</h4>

      <div className="space-y-4">
        <h5 className="text-xs font-semibold text-coval-text-muted">
          Inbound (sent to your agent)
        </h5>

        <div className="space-y-2">
          <FieldLabel tooltip={WEBSOCKET_TOOLTIPS.sendAudioTemplate}>
            Send Audio Template
          </FieldLabel>
          <JsonCodeEditor
            ariaLabel="Send Audio Template"
            placeholder={DEFAULT_AUDIO_TEMPLATE}
            value={metadata.send_audio_template || ""}
            onChange={handleAudioTemplateChange}
            rows={4}
            disabled={disabled}
            error={audioTemplateError}
            // Raw binary mode uses the bare `{{audio_data}}` template.
            lint={false}
          />
          <p className="text-xs text-coval-text-muted">
            JSON object template for sending audio. The template must contain
            exactly one{" "}
            <code className="bg-surface-1 px-1 rounded">
              {"{{audio_data}}"}
            </code>{" "}
            occurrence total, and that occurrence must be inside a JSON value.
            Use it alone for raw binary audio.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h5 className="text-xs font-semibold text-coval-text-muted">
          Outbound (received from your agent)
        </h5>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="space-y-2">
            <FieldLabel
              htmlFor="message-type-path"
              tooltip={WEBSOCKET_TOOLTIPS.messageTypePath}
            >
              Message Type Path
            </FieldLabel>
            <Input
              id="message-type-path"
              aria-label="Message Type Path"
              placeholder="type"
              value={metadata.message_type_path || ""}
              onChange={(e) =>
                onMetadataUpdate({ message_type_path: e.target.value })
              }
              disabled={disabled}
            />
            <p className="text-xs text-coval-text-muted">
              JSON path to the message type field (default: &quot;type&quot;)
            </p>
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-message-type"
              tooltip={WEBSOCKET_TOOLTIPS.audioMessageType}
            >
              Audio Message Type
            </FieldLabel>
            <Input
              id="audio-message-type"
              aria-label="Audio Message Type"
              placeholder="audio_chunk"
              value={metadata.audio_message_type_value || ""}
              onChange={(e) =>
                onMetadataUpdate({
                  audio_message_type_value: e.target.value,
                })
              }
              disabled={disabled}
            />
            <p className="text-xs text-coval-text-muted">
              Value indicating an audio message. Use &apos;*&apos; to match any
              message type (default: &quot;audio_chunk&quot;)
            </p>
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-data-path"
              tooltip={WEBSOCKET_TOOLTIPS.audioDataPath}
            >
              Audio Data Path
            </FieldLabel>
            <Input
              id="audio-data-path"
              aria-label="Audio Data Path"
              placeholder="data"
              value={metadata.audio_data_path || ""}
              onChange={(e) =>
                onMetadataUpdate({ audio_data_path: e.target.value })
              }
              disabled={disabled}
            />
            <p className="text-xs text-coval-text-muted">
              JSON path to the audio data field (default: &quot;data&quot;)
            </p>
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-encoding"
              tooltip={WEBSOCKET_TOOLTIPS.audioEncoding}
            >
              Audio Encoding
            </FieldLabel>
            <Select
              value={metadata.audio_encoding || "pcm"}
              onValueChange={(value) =>
                onMetadataUpdate({ audio_encoding: value })
              }
              disabled={disabled}
            >
              <SelectTrigger id="audio-encoding" aria-label="Audio Encoding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIO_ENCODING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-coval-text-muted">
              Encoding format of received audio data (default: pcm)
            </p>
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="receive-audio-channels"
              tooltip={WEBSOCKET_TOOLTIPS.receiveAudioChannels}
            >
              Channels
            </FieldLabel>
            <Select
              value={String(
                metadata.receive_audio_channels ??
                  DEFAULT_RECEIVE_AUDIO_CHANNELS,
              )}
              onValueChange={(value) =>
                onMetadataUpdate({ receive_audio_channels: Number(value) })
              }
              disabled={disabled}
            >
              <SelectTrigger id="receive-audio-channels" aria-label="Channels">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECEIVE_AUDIO_CHANNELS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  )
}
